import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CompetencyRow {
  readonly competencyId: string;
  readonly competencyCode: string;
  readonly competencyName: string;
  readonly category: string;
  readonly employeeRef: string | null;
  readonly requiredLevel: string | null;
  readonly currentLevel: string | null;
  readonly gapLevel: number | null;
  readonly status: string;
}

const STATUSES = ['', 'ACTIVE', 'UNDER_REVIEW', 'CLOSED', 'ARCHIVED'];
const CATEGORIES = ['', 'PROCESS', 'PRODUCT', 'POLICY', 'TECHNICAL', 'COMPLIANCE', 'SALES', 'FINANCE', 'HR', 'OPERATIONS', 'CUSTOMER', 'TRAINING', 'GENERAL'];
const SKILL_LEVELS = ['NONE', 'BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'];

/**
 * M35 — Competency Dashboard (§33). Register of employee competencies (T474)
 * carrying the required and current proficiency levels (NONE → EXPERT) and the
 * computed skill gap (required − current). Administrators recompute the gap
 * for a competency via the assess-gap action. Filterable by category, status
 * and employee. Tenant isolation is enforced server-side (LAW 4).
 */
export function CompetencyDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CompetencyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [employeeRef, setEmployeeRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('TECHNICAL');
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editRequired, setEditRequired] = useState('NONE');
  const [editCurrent, setEditCurrent] = useState('NONE');

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    if (employeeRef) params.set('employeeRef', employeeRef);
    const qs = params.toString();
    apiRequest<{ data: CompetencyRow[]; total: number }>(`/v1/kms/competencies${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, category, status, employeeRef]);

  useEffect(() => load(), [load]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(t('m35.competency.nameRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/competencies`, {
        method: 'POST',
        body: { competencyName: newName.trim(), category: newCategory },
        token,
      });
      setNotice(t('m35.common.created', { name: newName.trim() }));
      setNewName('');
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.competency.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: CompetencyRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(r.competencyId);
    try {
      await apiRequest(`/v1/kms/competencies/${r.competencyId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.competencyCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.competency.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const beginEdit = (r: CompetencyRow): void => {
    setEditId(r.competencyId);
    setEditRequired(r.requiredLevel ?? 'NONE');
    setEditCurrent(r.currentLevel ?? 'NONE');
    setActionError(null);
    setNotice(null);
  };

  const saveEdit = async (r: CompetencyRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(r.competencyId);
    try {
      await apiRequest(`/v1/kms/competencies/${r.competencyId}`, {
        method: 'PUT',
        body: { requiredLevel: editRequired, currentLevel: editCurrent },
        token,
      });
      setEditId(null);
      setNotice(t('m35.common.notice', { code: r.competencyCode, label: t('m35.common.act.updated') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.competency.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const assessGap = async (competencyId: string): Promise<void> => {
    setActionError(null);
    setBusyId(competencyId);
    try {
      await apiRequest<{ data: { gapLevel: number } }>(`/v1/kms/competencies/${competencyId}/assess-gap`, { method: 'POST', token });
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.competency.notAuthorisedAssess'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="competency-dashboard">
      <h1>{t('m35.competency.title')}</h1>

      <form aria-label="competency-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.competency.competencyName')}
          <input aria-label="field-competency-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </label>
        <label>
          {t('m35.common.category')}
          <select aria-label="field-competency-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
            {CATEGORIES.filter((c) => c).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="submit-competency" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.competency.createBtn')}</Button>
      </form>
      {notice && <p aria-label="competency-action-notice">{notice}</p>}

      <div className="filters">
        <label>
          {t('m35.common.category')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.employee')}
          <input aria-label="filter-employee" value={employeeRef} onChange={(e) => setEmployeeRef(e.target.value)} placeholder={t('m35.cert.employeePh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {actionError && <p role="alert" aria-label="competency-action-error">{actionError}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="competency-count">{t('m35.competency.count', { n: total })}</p>
          <DataTable<CompetencyRow>
            ariaLabel="competency-table"
            testId="competency-table"
            rowKey={(r) => r.competencyId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-category': r.category })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.competencyCode },
              { key: 'name', header: t('m35.common.name'), render: (r) => r.competencyName },
              { key: 'category', header: t('m35.common.category'), render: (r) => r.category },
              { key: 'employee', header: t('m35.common.employee'), render: (r) => r.employeeRef ?? '—' },
              { key: 'required', header: t('m35.competency.colRequired'), render: (r) => r.requiredLevel ?? '—' },
              { key: 'current', header: t('m35.competency.colCurrent'), render: (r) => r.currentLevel ?? '—' },
              { key: 'gap', header: t('m35.competency.colGap'), render: (r) => r.gapLevel ?? '—', cellAriaLabel: (r) => `competency-gap-${r.competencyCode}` },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'action', header: t('m35.common.actionCol'), render: (r) => (
                editId === r.competencyId ? (
                  <>
                    <select aria-label={`edit-required-${r.competencyCode}`} value={editRequired} onChange={(e) => setEditRequired(e.target.value)}>
                      {SKILL_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select aria-label={`edit-current-${r.competencyCode}`} value={editCurrent} onChange={(e) => setEditCurrent(e.target.value)}>
                      {SKILL_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Button variant="primary" type="button" aria-label={`save-${r.competencyCode}`} disabled={busyId === r.competencyId} onClick={() => void saveEdit(r)}>{t('m35.competency.btnSave')}</Button>
                    <Button variant="danger" type="button" aria-label={`cancel-${r.competencyCode}`} onClick={() => setEditId(null)}>{t('m35.competency.btnCancel')}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" type="button" aria-label={`edit-${r.competencyCode}`} disabled={busyId === r.competencyId} onClick={() => beginEdit(r)}>{t('m35.competency.btnEdit')}</Button>
                    <Button variant="secondary" type="button" aria-label={`assess-gap-${r.competencyCode}`} disabled={busyId === r.competencyId} onClick={() => void assessGap(r.competencyId)}>{t('m35.competency.btnAssessGap')}</Button>
                    <Button variant="danger" type="button" aria-label={`delete-${r.competencyCode}`} disabled={busyId === r.competencyId} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                  </>
                )
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
